import { keyframes, Stack, type StackProps, styled } from "@mui/material";
import { forwardRef } from "react";

const fluidMovement = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const WavyBorderStack = styled(Stack, {
  shouldForwardProp: (prop) =>
    prop !== "animationDuration" &&
    prop !== "animationDelay" &&
    prop !== "borderWidth",
})<{ animationDuration: string; animationDelay: string; borderWidth: string }>(
  ({ theme, animationDuration, animationDelay, borderWidth }) => ({
    border: `${borderWidth} solid transparent`,
    background: `
    linear-gradient(${theme.palette.common.white}, ${theme.palette.common.white}) padding-box, 
     linear-gradient(78deg, ${theme.palette.primary.main} 5.99%, ${theme.palette.accent.main} 104.08%) border-box
  `,
    backgroundSize: "200% 200%",
    animation: `${fluidMovement} ${animationDuration} ease infinite`,
    animationDelay,
    animationFillMode: "backwards",
    "@media (prefers-reduced-motion: reduce)": {
      animation: "none",
    },
    borderRadius: 12,
  }),
);

export interface AnimatedGradientBorderProps extends StackProps {
  /** Speed of the gradient sweep. Defaults to "4s". */
  animationDuration?: string;
  /** Delay before the sweep starts. Defaults to "2000ms". */
  animationDelay?: string;
  /** Thickness of the gradient border. Defaults to "1px". */
  borderWidth?: string;
}

export const AnimatedGradientBorder = forwardRef<
  HTMLDivElement,
  AnimatedGradientBorderProps
>(function AnimatedGradientBorder(
  {
    children,
    animationDuration = "4s",
    animationDelay = "2000ms",
    borderWidth = "1px",
    ...props
  },
  ref,
) {
  return (
    <WavyBorderStack
      ref={ref}
      animationDuration={animationDuration}
      animationDelay={animationDelay}
      borderWidth={borderWidth}
      {...props}
    >
      {children}
    </WavyBorderStack>
  );
});
